/*
  CSS propio de Stela sobre componentes de Optimus que no se puede expresar con tokens del preset
  (ITSM #1839680, lote 6.6). Es la clave `css` de `StelaPreset`: Optimus la inyecta en la capa
  `optimus`, así que pierde frente al CSS de componente de Angular y frente a la capa `app`.
  Viene de los antiguos theme-kit.prime-overrides.css y theme-kit.app.css.
*/
export const STELA_PRESET_CSS = `
/* --- Inputs de formulario más compactos (13px) --- */

.p-inputtext,
.p-select-label,
.p-multiselect-label,
.p-autocomplete-input,
.p-textarea,
.p-password-input {
  font-size: var(--stela-font-size-sm) !important;
}

/* --- Select / MultiSelect: altura mínima ---
   --p-form-field-padding-y está pensado para inputs de texto; p-select y
   p-multiselect no tenían ningún min-height propio, así que el control
   sin selección (label vacío/placeholder) podía quedar visualmente más
   bajo que un input de texto en la misma fila. */

.p-select,
.p-multiselect {
  min-height: calc(var(--stela-font-size-sm) * 1.4 + var(--p-form-field-padding-y) * 2) !important;
}

.p-select-sm,
.p-multiselect-sm {
  min-height: calc(var(--stela-font-size-sm) * 1.4 + var(--p-form-field-sm-padding-y) * 2) !important;
}

/* --- AutoComplete múltiple: fondo del contenedor y de las fichas ---
   El token --p-autocomplete-background ya apunta a la superficie oscura, pero el
   contenedor de fichas (un <ul>) seguía saliendo blanco: PrimeNG lo pinta por una
   vía que ese token no cubre. Se fija aquí sobre el propio selector, como el borde
   de foco de abajo. Las fichas van contra --stela-bg-muted resuelto *en el elemento*,
   que dentro de <body> sí toma el valor oscuro. */

.p-autocomplete-input-multiple {
  background: var(--stela-surface) !important;
  color: var(--stela-text-primary) !important;
}

.p-autocomplete-chip.p-chip {
  background: var(--stela-bg-muted) !important;
  color: var(--stela-text-primary) !important;
}

/* --- Foco de los campos: dorado, nunca el verde del preset ---
   Los presets de PrimeNG derivan el borde de foco de su color primario, que de fábrica es
   esmeralda; el puente ya mapea los tokens (--p-inputtext-focus-border-color y compañía), pero
   el contenedor de fichas del autocompletado se seguía viendo con el borde verde sobre el fondo
   oscuro. Esto lo fija en el propio selector, que gana venga el verde del token o de una hoja
   heredada, y apaga el anillo del navegador, que ya no aporta nada con el borde marcado. */

.p-inputtext:focus,
.p-inputtext:focus-visible,
.p-textarea:focus,
.p-autocomplete-input-multiple:focus-within,
.p-autocomplete:focus-within .p-autocomplete-input-multiple {
  border-color: var(--stela-gold) !important;
  outline-color: transparent !important;
  box-shadow: none !important;
}

/* --- MultiSelect en modo chip: todos los chips visibles y a varias líneas ---
   El rótulo del multiselect es 'nowrap' con 'overflow: hidden', así que los chips se recortaban
   en cuanto no cabían en una línea. Con esto el control crece hacia abajo, como el de la pantalla
   antigua. El otro corte (a partir del cuarto seleccionado el control pasaba a
   "N elementos seleccionados") es 'maxSelectedLabels', que vale 3 por defecto y hay que anular en
   la plantilla con '[maxSelectedLabels]="null"'. */

.p-multiselect-display-chip .p-multiselect-label {
  flex-wrap: wrap;
  white-space: normal;
}

/* --- DatePicker input icon size --- */

.p-datepicker-input-icon {
  width: 0.875rem !important;
  height: 0.875rem !important;
}

/* --- DatePicker contrast fixes ---
   Lara dark theme renders day numbers, weekday headers and hover states with
   insufficient contrast against the dark panel background. */

/* Day numbers (unselected) */

.p-datepicker-day {
  color: var(--stela-fg) !important;
}

/* Selected day — keep preset colour */

.p-datepicker-day-selected .p-datepicker-day {
  color: var(--p-datepicker-date-selected-color, #fff) !important;
}

/* Weekday header letters (L M X J V S D) */

.p-datepicker-weekday {
  color: var(--stela-fg) !important;
  opacity: 0.65;
}

/* Hover on a non-selected day cell */

.p-datepicker-day-cell:not(.p-datepicker-day-selected):hover .p-datepicker-day {
  color: var(--stela-fg) !important;
}

/* --- Overflow containment for panels and tabs ---
   PrimeNG Panel uses display:grid and p-tabs uses display:flex (column).
   Without min-width:0, their grid/flex items inherit min-width:auto, which
   resolves to the item's min-content size (= the table's full width).
   This causes the panel/tabs to expand beyond the viewport instead of
   letting the inner overflow-x:auto scroll region do its job.
   Same pattern already applied to .tk-stela-layout-content in the shell. */

.p-panel-content-wrapper {
  min-width: 0;
}

.p-tabpanels {
  min-width: 0;
}

.p-panel {
    margin-bottom: 25px;
}

.p-panel-header {
    font-size: var(--stela-font-size-md, 14px);
    margin: 0 0 0px;
    text-overflow: ellipsis;
    font-weight: 600;
}

/* Block titles (Stela 2 prototype, .card-head .card-title): Playfair 16px/600.
   Lara exposes only fontWeight for panel/card titles, so the typeface lives
   here. Custom header templates put their title in an h1-h6. */


.p-tooltip .p-tooltip-text {
    white-space: nowrap;
    width: fit-content;
}

.p-dialog-footer {
    border-top: 1px solid var(--stela-border-color);
}

.p-dialog-header {
    border-bottom: 1px solid var(--stela-border-color);
}

.p-dialog-content {
    margin: 15px;
}

/* --- Tabs (todos los temas) ---
   Diseño "subrayado" (sin caja/fondo por pestaña, borde inferior de 2px
   en dorado para la activa), siguiendo el componente Tabs de referencia
   en docs/design/stela-2/project/pages-shared.jsx y su uso real en
   pages-edit-system.jsx. Sin scope de body.*-theme: usa los tokens
   --stela-* que ya resuelven su valor por skin/tema activo, así que un
   único bloque sirve para light, dark y el resto de skins — sustituye
   tanto al bloque anterior (con fondo/caja) como al bloque específico
   de dark-theme que existía antes, ahora unificados aquí. */

/* Pestañas: colores, padding y borde vienen de StelaPreset (tabs). Aquí solo
   lo que no tiene token. */

.p-tabs .p-tab {
    font-size: 13.5px;
}

.p-tabs .p-tab-active,
.p-tabs .p-tab.p-tab-active {
    font-weight: 600;
}

/* --- Dark Theme: DataTable --- */

/*
 * Column headers — Stela 2 listing look:
 * uppercase + tracking + muted colour so the data below stands out, while
 * still using the theme tokens so dark mode still looks correct.
 */

.p-datatable .p-datatable-thead > tr > th {
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 0.74rem;
    font-weight: 600;
}

/*
 * The 'pTemplate="footer"' slot of <p-table> renders inside <tfoot> and was
 * showing as a hard white strip on dark mode (PrimeNG default white surface
 * leaks through). Force the dark surface tokens on every tfoot descendant.
 */

/* --- Dark Theme: Menu / ContextMenu ---
 *
 * OJO con los nombres de clase: PrimeNG renombró las partes internas de los menús a partir de la
 * v18 ('p-menuitem-link' → 'p-menu-item-link', y en ContextMenu a 'p-contextmenu-item-link'). El
 * contenedor '.p-menu' sí conservó el nombre, así que estas reglas seguían pintando el panel de
 * oscuro mientras las del texto no casaban con nada: el item se quedaba con el color del puente
 * '--p-menu-item-color', resuelto en ':root' con los tokens del tema claro, y salía azul marino
 * sobre el panel oscuro — ilegible salvo al pasar el ratón, porque el fondo de hover también
 * venía del puente y era claro.
 */

/* Acción destructiva de los menús contextuales del gestor de archivos ('styleClass: 'p-menuitem-danger'').
 * Va en CSS global: el overlay se añade a <body> ('appendTo="body"'), fuera del host del componente, así que
 * una regla ':host ::ng-deep' nunca casaría. */

.p-contextmenu .p-menuitem-danger > .p-contextmenu-item-content {
    color: var(--stela-danger, #BA151B);
}

.p-contextmenu .p-menuitem-danger > .p-contextmenu-item-content:hover {
    background: color-mix(in srgb, var(--stela-danger, #BA151B) 12%, transparent);
}

/* En oscuro, #BA151B sobre el panel (#2A1E2E) da 2,43:1; #F87171 da 5,74:1. */

/* Inputs deshabilitados en oscuro: sin la opacidad de deshabilitado de Optimus
   (fondo y color ya vienen de inputtext/textarea en StelaPreset). */

html[data-color-scheme="dark"] .p-inputtext:disabled,
html[data-color-scheme="dark"] .p-textarea:disabled,
html[data-color-scheme="dark"] .p-inputtext.p-disabled,
html[data-color-scheme="dark"] .p-textarea.p-disabled {
    cursor: not-allowed;
    opacity: 1;
}

html[data-color-scheme="dark"] .p-contextmenu .p-menuitem-danger > .p-contextmenu-item-content {
    color: #F87171;
}

/* --- Improved Panel Styling --- */

.p-panel.p-panel-toggleable .p-panel-header {
    cursor: pointer;
    user-select: none;
}

/* p-tree-node-label: por defecto PrimeNG no lo hace flex y el contenido
   del pTemplate queda pegado al icono. Forzamos display flex + flex 1 +
   width 100% en toda la cadena (content → label → primer hijo del template)
   para que el 'ml-auto' del template empuje el badge "X cargos" al borde derecho. */

.p-tree .p-tree-node-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
}

.p-tree .p-tree-node-content > .p-tree-node-label {
    display: flex;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
}

.p-tree .p-tree-node-content > .p-tree-node-label > * {
    width: 100%;
}
`;
